import { API_BASE, ApiScope, request, uploadWithProgress } from './client';
import type { InitiateUploadResponse } from './types';

/**
 * Upload em três passos — o binário NUNCA passa pelo CORE.
 *
 *   1. `initiate`  → o MEDIA abre a sessão e valida o que foi DECLARADO
 *   2. `PUT /uploads/:id/content` → o conteúdo, com progresso
 *
 * E PARA AQUI. A terceira fase — conferir tamanho, SHA-256, magic bytes e
 * antivírus, e promover ao bucket WORM — é do CORE, no mesmo ato em que ele
 * cria o documento, a nova versão ou a não-conformidade. O cliente devolve o
 * `uploadId`; quem o resgata é quem grava o registro que o referencia.
 *
 * O passo 2 ia DIRETO ao armazenamento, por URL pré-assinada. Não vai mais: em
 * cluster o endereço do storage só resolve dentro dele, e o navegador recebia
 * uma URL que não existia para ele — o envio falhava sem deixar rastro em log
 * de serviço nenhum. Quem entrega os bytes ao storage agora é o MEDIA.
 *
 * O oposto do legado, em que o controller do GED recebia `$_FILES` e mandava
 * direto para o Drive com `mimeType: 'application/octet-stream'`, sem verificar
 * nada (ged.md §11.3).
 *
 * O SHA-256 é calculado NO CLIENTE antes de subir. Não é controle de segurança
 * — o servidor recalcula e recusa se divergir (`MEDIA.FILE_CHECKSUM_MISMATCH`);
 * serve para detectar corrupção em trânsito sem gastar uma subida inteira.
 */
export const mediaApi = {
  async sha256(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  },

  initiate(input: {
    filename: string;
    contentType: string;
    sizeBytes: number;
    sha256: string;
    kind?: 'DOCUMENT_BINARY' | 'EXPORT_PDFA' | 'THUMBNAIL';
  }): Promise<InitiateUploadResponse> {
    return request<InitiateUploadResponse>({
      scope: ApiScope.MEDIA,
      path: '/uploads/initiate',
      method: 'POST',
      // `organizationId` e `uploadedBy` são injetados pelo gateway a partir do
      // JWT: o cliente não tem como (nem deve) declarar de quem é o upload.
      body: { ...input, kind: input.kind ?? 'DOCUMENT_BINARY' },
    });
  },

  /** Executa o fluxo completo, reportando progresso. */
  async uploadFile(params: {
    readonly file: File;
    readonly onProgress?: (percent: number) => void;
    readonly signal?: AbortSignal;
  }): Promise<{ uploadId: string; fileName: string; sha256: string }> {
    const { file, onProgress, signal } = params;

    const checksum = await mediaApi.sha256(file);

    const ticket = await mediaApi.initiate({
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      sha256: checksum,
    });

    await uploadWithProgress({
      url: `${API_BASE}/uploads/${ticket.uploadId}/content`,
      method: 'PUT',
      file,
      onProgress,
      signal,
    });

    /*
     * NÃO finaliza aqui. Quem finaliza é o CORE, ao criar o documento, a nova
     * versão ou a não-conformidade — `commitUpload` em `media.client.ts`.
     *
     * Finalizar dos dois lados era o defeito: o navegador promovia o objeto ao
     * WORM, o CORE tentava promover de novo e recebia
     * `COMMON.CONFLICT — Este upload já foi finalizado.`, de modo que a criação
     * do documento falhava DEPOIS de o arquivo já estar retido por dez anos.
     * Ficava um objeto órfão no bucket imutável, sem documento que o citasse.
     *
     * A regra: o cliente ENTREGA os bytes; quem promove ao acervo é quem cria o
     * registro que os referencia, na mesma operação.
     */
    return { uploadId: ticket.uploadId, fileName: file.name, sha256: checksum };
  },
};
